import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { filterNil, guid } from '@datorama/akita';
import { AkitaNgFormsManager } from '@datorama/akita-ng-forms-manager';
import {
	ComponentFormController,
	MessagingService,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import {
	finalize,
	first,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs/operators';

import { UserApplicationPort } from '../../../../../application/user/port/user-application.port';
import {
	CoachSettingsBiographyState,
	CoachSettingsBiographyStore,
} from './coach-settings-biography.store';

@Injectable()
@UntilDestroy()
export class CoachSettingsBiographyController extends ComponentFormController<
	CoachSettingsBiographyState,
	any
> {
	public isSaving$: Observable<boolean>;
	public editorConfig$: Observable<any>;

	constructor(
		private _fb: FormBuilder,
		formsManager: AkitaNgFormsManager,
		store: CoachSettingsBiographyStore,
		private _messagingService: MessagingService,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort
	) {
		super(
			store,
			formsManager,
			`coach-settings-biography-${guid()}`,
			_fb.group({ biography: ['', Validators.required] })
		);

		this._initObservables();
		this._initListeners();
	}

	public updateBiography(): Promise<void> {
		this.store.update({ isSaving: true });

		return this._userApplicationPort
			.observeSessionUserId()
			.pipe(
				switchMap((userId) =>
					this._userApplicationPort.updateCoachBiography({
						biography: this.form.value.biography,
						userId,
					})
				),
				finalize(() => this.store.update({ isSaving: false })),
				tap(() =>
					this._messagingService.publishMessage({
						summaryKey:
							'presentation.app.page.settings.coach.biography.saved.summary',
						detailKey: 'presentation.app.page.settings.coach.biography.saved.detail',
						severity: 'success',
					})
				),
				first()
			)
			.toPromise();
	}

	private _initListeners() {
		this._userApplicationPort
			.observeSessionUser()
			.pipe(
				filterNil,
				switchMap((user) => user.organization$),
				tap((org) => this.form.patchValue({ biography: org.description || '' })),
				untilDestroyed(this)
			)
			.subscribe();
	}

	private _initObservables() {
		this.editorConfig$ = this._translateService.onLangChange.pipe(
			startWith({ lang: this._translateService.currentLang }),
			map((event) => event.lang),
			map((language) => ({
				placeholder: this._translateService.instant(
					'presentation.app.page.settings.coach.biography.placeHolder'
				),
				toolbar: ['bold', 'italic'],
				language: language,
			})),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isSaving$ = this.select('isSaving');
	}
}
